import { ref } from "vue";
import { captureException } from "@sentry/vue";
import { adminV1OrganizationsAssociatedContactsRetrieve } from "@/api/admin-associated-contacts/admin-associated-contacts";
import { adminV1OrganizationsPaymentsBillingProfilesRetrieve } from "@/api/admin-payments-billing-profiles/admin-payments-billing-profiles";
import { useCurrentOrganization } from "@/composables/useCurrentOrganization";
import { SELF_BILLING_CONTACT_ID } from "@/modules/payments/constants";
import type {
  AdminAssociatedContactDetail,
  AdminBillingProfileDetail,
} from "@/api/model";

export const useAssociatedContactDetail = () => {
  const { currentOrganizationId } = useCurrentOrganization();

  const contactDetail = ref<AdminAssociatedContactDetail | null>(null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  const retrieveBillingProfile = async (
    organizationId: number,
    billingProfileId: string
  ): Promise<AdminBillingProfileDetail | null> => {
    try {
      return await adminV1OrganizationsPaymentsBillingProfilesRetrieve(
        organizationId,
        billingProfileId
      );
    } catch (bpError) {
      console.warn(
        "[useAssociatedContactDetail] Failed to fetch billing profile",
        bpError
      );
      captureException(bpError, {
        extra: {
          message: "Failed to fetch billing profile detail",
        },
      });
      return null;
    }
  };

  const fetchContactDetail = async (contactId: number): Promise<void> => {
    if (!currentOrganizationId.value) {
      error.value = "No organization ID found";
      console.error("No organization ID found");
      return;
    }

    isLoading.value = true;
    error.value = null;

    try {
      const acResult = await adminV1OrganizationsAssociatedContactsRetrieve(
        currentOrganizationId.value,
        contactId
      );

      const billingProfileId =
        typeof acResult.billing_profile === "object" &&
        acResult.billing_profile !== null
          ? (acResult.billing_profile as unknown as { id: string }).id
          : acResult.billing_profile;

      // The BillingProfile is the only source of billing data (the AC's own
      // billing fields are being dropped from the backend), so without it the
      // contact keeps its identity fields but has no payment methods.
      const bpDetail = billingProfileId
        ? await retrieveBillingProfile(
            currentOrganizationId.value,
            billingProfileId
          )
        : null;

      contactDetail.value = {
        ...acResult,
        payment_methods: bpDetail?.payment_methods ?? [],
        payabli_customer_url: bpDetail?.payabli_customer_url ?? null,
        payabli_customer_number: bpDetail?.payabli_customer_number ?? null,
      };
    } catch (e: any) {
      error.value = e.message || "Failed to fetch contact detail";
      console.error("Failed to fetch contact detail:", e);
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Load participant `profileId`'s own billing profile into `contactDetail`,
   * shaped like a contact whose id is SELF_BILLING_CONTACT_ID.
   */
  const fetchBillingProfileDetail = async (
    billingProfileId: string,
    profileId: number
  ): Promise<void> => {
    if (!currentOrganizationId.value) {
      error.value = "No organization ID found";
      console.error("No organization ID found");
      return;
    }

    isLoading.value = true;
    error.value = null;

    try {
      const bpDetail = await retrieveBillingProfile(
        currentOrganizationId.value,
        billingProfileId
      );
      if (!bpDetail) {
        contactDetail.value = null;
        error.value = "Failed to fetch billing profile detail";
        return;
      }

      contactDetail.value = {
        id: SELF_BILLING_CONTACT_ID,
        name: bpDetail.name,
        email: bpDetail.email ?? "",
        phone_number: bpDetail.phone_number,
        organization: bpDetail.organization,
        profile: profileId,
        profiles: bpDetail.profiles,
        picture: bpDetail.picture,
        payment_methods: bpDetail.payment_methods,
        payabli_customer_url: bpDetail.payabli_customer_url,
        payabli_customer_number: bpDetail.payabli_customer_number,
        billing_profile: { id: bpDetail.id },
      };
    } finally {
      isLoading.value = false;
    }
  };

  return {
    contactDetail,
    isLoading,
    error,
    fetchContactDetail,
    fetchBillingProfileDetail,
  };
};
